import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { DIVISIBILITY_RULES } from "./rules";

const MiniTile = ({ status, children }) => (
  <span
    className="inline-flex h-9 w-9 items-center justify-center rounded-[5px] font-mono text-lg font-medium text-white"
    style={{ background: `var(--numex-${status})` }}
  >
    {children}
  </span>
);

function FeedbackRow({ status, title, desc }) {
  return (
    <div className="flex items-center gap-3">
      <span
        className="inline-block h-6 w-6 shrink-0 rounded-[5px]"
        style={{ background: `var(--numex-${status})` }}
        aria-hidden
      />
      <span className="text-sm leading-snug text-[var(--numex-ink)]">
        <b className="font-semibold">{title}</b> — {desc}
      </span>
    </div>
  );
}

export default function HowToPlay({ open, onOpenChange, onPlay, divisor }) {
  const rule = DIVISIBILITY_RULES[divisor];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-sm rounded-xl max-h-[90vh] overflow-y-auto"
        data-testid="how-to-play-modal"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="items-center text-center">
          <div className="font-mono text-lg font-bold leading-none tracking-tight text-[var(--numex-ink)]">
            ÷X
          </div>
          <DialogTitle className="mt-1 text-2xl font-extrabold tracking-[0.16em] pl-[0.16em]">
            NUMEX
          </DialogTitle>
          <p className="mt-0.5 text-xs text-[var(--numex-muted)] tracking-wide">
            5 digits. 5 tries. One number a day.
          </p>
          <DialogDescription className="sr-only">
            How to play NUMEX, the daily 5-digit number puzzle.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-1">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-[var(--numex-muted)]">
              How to play
            </p>
            <p className="mt-1.5 text-sm leading-relaxed text-[var(--numex-ink)]">
              Find today&apos;s secret 5-digit number in 5 tries.
            </p>
            <p className="mt-1.5 text-sm leading-relaxed text-[var(--numex-ink)]">
              There&apos;s one twist: every guess must be divisible by today&apos;s divisor.
            </p>
            <p className="mt-1.5 text-sm leading-relaxed text-[var(--numex-muted)]">
              If today&apos;s rule is ÷7, every number you enter must be divisible by 7.
            </p>
          </div>

          {/* Example tiles */}
          <div className="flex justify-center gap-1.5" aria-hidden data-testid="how-to-play-example">
            <MiniTile status="green">4</MiniTile>
            <MiniTile status="absent">4</MiniTile>
            <MiniTile status="gold">1</MiniTile>
            <MiniTile status="absent">3</MiniTile>
            <MiniTile status="green">0</MiniTile>
          </div>

          <div className="space-y-2.5">
            <FeedbackRow status="green" title="Green" desc="correct digit, correct position." />
            <FeedbackRow status="gold" title="Yellow" desc="correct digit, wrong position." />
            <FeedbackRow status="absent" title="Grey" desc="digit isn't in the number." />
          </div>

          <ul className="space-y-1 text-sm text-[var(--numex-muted)]">
            <li>Repeated digits are possible.</li>
            <li>A guess that breaks today&apos;s divisibility rule doesn&apos;t use an attempt.</li>
          </ul>

          {divisor && rule && (
            <div className="rounded-lg border border-[var(--numex-tile-idle-border)] bg-[#f7f6f1] px-4 py-3" data-testid="todays-rule">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-[var(--numex-muted)]">
                Today&apos;s rule — divisible by {divisor}
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-[var(--numex-ink)]">
                Every guess must be divisible by {divisor}.
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-[var(--numex-ink)]">{rule.rule}</p>
              <p className="mt-2 text-xs text-[var(--numex-muted)]">
                Tip: tap &quot;View rule&quot; beside today&apos;s divisor on the game screen whenever you need help.
              </p>
            </div>
          )}

          <button
            type="button"
            onClick={onPlay}
            data-testid="how-to-play-cta"
            className="numex-cta mt-1 w-full rounded-lg py-3 text-sm font-semibold tracking-widest"
          >
            PLAY NUMEX
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
